import React, { useEffect } from 'react';
import './AccountDeletionPage.css';

const AccountDeletionPage: React.FC = () => {
  useEffect(() => {
    // Set current year
    const yearElement = document.getElementById('currentYear');
    if (yearElement) {
      yearElement.textContent = new Date().getFullYear().toString();
    }
    
    // Set last updated date
    const lastUpdatedElement = document.getElementById('lastUpdated');
    if (lastUpdatedElement) {
      lastUpdatedElement.textContent = new Date().toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    }
  }, []);

  return (
    <div className="account-deletion-page">
      <div className="account-deletion-container">
        <header className="account-deletion-header">
          <h1>How to Delete Your Account</h1>
          <p className="last-updated">Last Updated: <span id="lastUpdated"></span></p>
        </header>

        <section>
          <p>
            If you wish to delete your account and all associated data from the BFM Neo mobile application, 
            please follow the steps below. We're sorry to see you go, but we respect your decision.
          </p>
          <div className="highlight warning">
            <p>
              <strong>Important:</strong> Account deletion is permanent and cannot be undone after the 30-day 
              archive period. Please ensure you want to proceed before completing these steps.
            </p>
          </div>
        </section>

        <section>
          <h2>Step-by-Step Instructions</h2>
          
          <div className="step">
            <div className="step-number">1</div>
            <div className="step-content">
              <h3>Install and Open the App</h3>
              <p>
                Make sure you have the BFM Neo mobile application installed on your device. If you don't have it 
                installed, download it from the App Store (iOS) or Google Play Store (Android).
              </p>
            </div>
          </div>

          <div className="step">
            <div className="step-number">2</div>
            <div className="step-content">
              <h3>Login to Your Account</h3>
              <p>
                Open the app and log in using your account credentials (email and password). You must be logged 
                in to access the account deletion option.
              </p>
            </div>
          </div>

          <div className="step">
            <div className="step-number">3</div>
            <div className="step-content">
              <h3>Navigate to Personal Details</h3>
              <p>
                Once logged in, look at the bottom navigation bar. You will see a person icon (👤) on the 
                bottom right corner. Tap on this icon to access your personal details page.
              </p>
            </div>
          </div>

          <div className="step">
            <div className="step-number">4</div>
            <div className="step-content">
              <h3>Scroll to the Delete Account Section</h3>
              <p>
                On the personal details page, scroll down to the bottom of the page. You will find a section 
                labeled "Delete Account" or similar.
              </p>
            </div>
          </div>

          <div className="step">
            <div className="step-number">5</div>
            <div className="step-content">
              <h3>Confirm Account Deletion</h3>
              <p>
                Follow the prompts in the Delete Account section to confirm that you want to delete your account. 
                You may be asked to confirm your decision or enter your password for security purposes.
              </p>
            </div>
          </div>
        </section>

        <section>
          <h2>What Happens After You Delete Your Account?</h2>
          
          <div className="timeline">
            <div className="timeline-item">
              <div className="timeline-marker"></div>
              <div className="timeline-content">
                <h3>Immediate (Day 0)</h3>
                <p>
                  Your account will be immediately deactivated and archived. You will no longer be able to 
                  access your account or use the app's features. Your data will be moved to an archived state.
                </p>
              </div>
            </div>

            <div className="timeline-item">
              <div className="timeline-marker"></div>
              <div className="timeline-content">
                <h3>30-Day Archive Period</h3>
                <p>
                  Your account and all associated data will remain in an archived state for 30 days. During 
                  this period, you can still log in to restore your account if you change your mind. Simply 
                  log in with your credentials, and you'll be given the option to restore your account.
                </p>
              </div>
            </div>

            <div className="timeline-item">
              <div className="timeline-marker"></div>
              <div className="timeline-content">
                <h3>Permanent Deletion (After 30 Days)</h3>
                <p>
                  After 30 days, your account and all associated data will be permanently deleted from our 
                  records. This includes:
                </p>
                <ul>
                  <li>Your account information (name, email, phone number)</li>
                  <li>Your spa information and configurations</li>
                  <li>Your app settings and preferences</li>
                  <li>Your service history and records</li>
                  <li>All other data associated with your account</li>
                </ul>
                <p>
                  <strong>This deletion is permanent and cannot be undone.</strong> You will need to create 
                  a new account if you wish to use the app again in the future.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section>
          <h2>Important Information</h2>
          
          <div className="info-box">
            <h3>Account Restoration</h3>
            <p>
              If you change your mind within 30 days of deleting your account, you can restore it by simply 
              logging in with your original credentials. The app will detect that your account is archived 
              and offer you the option to restore it.
            </p>
          </div>

          <div className="info-box">
            <h3>Data Backup</h3>
            <p>
              Before deleting your account, consider if you want to save any information that is important 
              to you. Once the 30-day archive period expires, all data will be permanently deleted and cannot 
              be recovered.
            </p>
          </div>

          <div className="info-box">
            <h3>Alternative: Contact Support</h3>
            <p>
              If you're having trouble accessing the app or the delete account option, or if you have questions 
              about the deletion process, please contact our support team. We're here to help.
            </p>
          </div>
        </section>

        <section>
          <h2>Frequently Asked Questions</h2>
          
          <div className="faq-item">
            <h3>Can I delete my account from the website?</h3>
            <p>
              Currently, account deletion must be performed through the mobile app. This ensures that you have 
              full access to your account and can verify your identity before deletion.
            </p>
          </div>

          <div className="faq-item">
            <h3>What if I can't find the delete account option?</h3>
            <p>
              Make sure you're logged in and have scrolled all the way to the bottom of the personal details 
              page. If you still can't find it, try updating the app to the latest version or contact support 
              for assistance.
            </p>
          </div>

          <div className="faq-item">
            <h3>Will deleting my account cancel any services?</h3>
            <p>
              Deleting your app account will not affect any physical spa services, warranties, or service 
              agreements you may have with Arctic Spas. This only removes your digital account and app access.
            </p>
          </div>

          <div className="faq-item">
            <h3>Can I delete my account if I forgot my password?</h3>
            <p>
              You need to be logged in to delete your account. If you've forgotten your password, use the 
              "Forgot Password" feature to reset it first, then follow the deletion steps.
            </p>
          </div>
        </section>

        <section>
          <h2>Need Help?</h2>
          
          <div className="contact-info">
            <p>If you need assistance with account deletion or have any questions, please contact us:</p>
            <p>
              <strong>Arctic Spas</strong><br />
              Email: <a href="mailto:app.support@arcticspas.com">app.support@arcticspas.com</a><br />
            </p>
            <p>
              We're here to help and will respond to your inquiries as soon as possible.
            </p>
          </div>
        </section>

        <footer className="account-deletion-footer">
          <p>&copy; <span id="currentYear"></span> Arctic Spas. All rights reserved.</p>
        </footer>
      </div>
    </div>
  );
};

export default AccountDeletionPage;

